import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MessageCircle, ShieldCheck } from "lucide-react";
import { MaskedLines } from "../components/Reveal";
import { useUI } from "../context/UIContext";
import { CONTACT, IMAGES } from "../data/content";

export const Hero = () => {
  const { openUpload, openLead } = useUI();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen bg-white overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroAbstract} alt="" className="w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/40" />
      </motion.div>

      {/* Glow */}
      <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-blue-500/10 blur-[120px] pointer-events-none" />

      <motion.div style={{ opacity }} className="relative max-w-[1400px] mx-auto px-6 sm:px-10 pt-40 pb-24 min-h-screen flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 self-start rounded-full border border-slate-200 bg-white/70 backdrop-blur-md px-4 py-2 text-xs text-slate-600 mb-8 shadow-sm"
        >
          <ShieldCheck className="h-3.5 w-3.5 text-blue-600" />
          Über 100 geprüfte Abrechnungen · 9 von 10 Kunden sparen
        </motion.div>

        <h1 className="text-navy-900 font-heading font-semibold tracking-tight leading-[0.98] text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
          <MaskedLines
            lines={[
              "Kostenlose Analyse",
              'Ihrer <span class="font-serif italic text-blue-700">Strom-</span> oder',
              '<span class="font-serif italic text-blue-700">Gasrechnung.</span>',
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 max-w-xl text-lg text-slate-600 font-light leading-relaxed"
        >
          Wir prüfen Ihren Tarif kostenlos und unabhängig – persönlich statt Portal-Dschungel.
          Klarheit in 24 Stunden.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            onClick={openUpload}
            data-testid="hero-upload-btn"
            className="group rounded-full bg-blue-700 hover:bg-blue-600 text-white px-8 py-4 font-medium transition-all duration-300 hover:-translate-y-1 flex items-center gap-2 shadow-lg shadow-blue-900/20"
          >
            Rechnung hochladen
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => openLead({ type: "beratung" })}
            data-testid="hero-beratung-btn"
            className="rounded-full bg-white text-navy-900 border border-slate-300 hover:border-blue-400 hover:text-blue-700 px-8 py-4 font-medium transition-colors duration-300 shadow-sm"
          >
            Kostenlose Beratung
          </button>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="rounded-full text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 px-6 py-4 font-medium transition-colors duration-300 flex items-center gap-2"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600"
          data-testid="hero-trust-row"
        >
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" /> 100&nbsp;% kostenlos & unverbindlich</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" /> Unabhängig – kein Callcenter</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-600" /> Antwort in 24&nbsp;Stunden</span>
        </motion.p>
      </motion.div>
    </section>
  );
};
